import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import SuperAdminLayout from '../components/SuperAdminLayout'
import LoadingSpinner from '../../../components/LoadingSpinner'

export default function SuperAdminMembers() {
  const [members, setMembers] = useState([])
  const [filtered, setFiltered] = useState([])
  const [clubs, setClubs] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [clubFilter, setClubFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState('')

  useEffect(() => {
    const fetchData = async () => {
      const { data: membersData } = await supabase
        .from('supporters')
        .select('id, full_name, email, membership_id, status, role, club_id, chapter_id, joined_at, profile_photo, chapters(name), clubs(name)')
        .order('joined_at', { ascending: false })
        .limit(500)

      const { data: clubsData } = await supabase
        .from('clubs')
        .select('id, name')
        .order('name')

      if (membersData) { setMembers(membersData); setFiltered(membersData) }
      if (clubsData) setClubs(clubsData)
      setLoading(false)
    }
    fetchData()
  }, [])

  useEffect(() => {
    let result = members
    if (search) {
      result = result.filter(
        (m) =>
          m.full_name?.toLowerCase().includes(search.toLowerCase()) ||
          m.membership_id?.toLowerCase().includes(search.toLowerCase()) ||
          m.email?.toLowerCase().includes(search.toLowerCase())
      )
    }
    if (clubFilter) result = result.filter((m) => m.club_id === clubFilter)
    if (statusFilter) result = result.filter((m) => m.status === statusFilter)
    if (roleFilter) result = result.filter((m) => m.role === roleFilter)
    setFiltered(result)
  }, [search, clubFilter, statusFilter, roleFilter, members])

  const handleExport = () => {
    const csv = [
      ['Member ID', 'Name', 'Email', 'Role', 'Club', 'Chapter', 'Status', 'Joined'],
      ...filtered.map((m) => [
        m.membership_id,
        m.full_name,
        m.email,
        m.role,
        m.clubs?.name || 'N/A',
        m.chapters?.name || 'N/A',
        m.status,
        new Date(m.joined_at).toLocaleDateString(),
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'all-members.csv'
    a.click()
  }

  const roleBadge = (role) => {
    const styles = {
      supporter: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      club_admin: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      chapter_coordinator: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      super_admin: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    }
    const labels = {
      supporter: 'Supporter',
      club_admin: 'Admin',
      chapter_coordinator: 'Coordinator',
      super_admin: 'Super Admin',
    }
    return (
      <span className={`text-xs font-bold uppercase tracking-widest px-2 py-0.5 shrink-0 ${styles[role] || 'bg-gray-100 text-gray-500'}`}>
        {labels[role] || role}
      </span>
    )
  }

  const inputClass = 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-600'

  const counts = {
    all: members.length,
    supporter: members.filter(m => m.role === 'supporter').length,
    club_admin: members.filter(m => m.role === 'club_admin').length,
    chapter_coordinator: members.filter(m => m.role === 'chapter_coordinator').length,
  }

  if (loading) return <LoadingSpinner fullScreen message="Loading members..." />

  return (
    <SuperAdminLayout>
      <div className="mb-6 lg:mb-8 flex justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-4xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">
            All Users
          </h1>
          <p className="text-gray-500 text-sm">{filtered.length} of {members.length} users across all clubs</p>
        </div>
        <button
          onClick={handleExport}
          className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-3 font-bold uppercase tracking-widest text-xs sm:text-sm transition-colors whitespace-nowrap"
        >
          Export CSV
        </button>
      </div>

      {/* Role Quick Filters */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { label: `All (${counts.all})`, value: '' },
          { label: `Supporters (${counts.supporter})`, value: 'supporter' },
          { label: `Admins (${counts.club_admin})`, value: 'club_admin' },
          { label: `Coordinators (${counts.chapter_coordinator})`, value: 'chapter_coordinator' },
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setRoleFilter(tab.value)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
              roleFilter === tab.value
                ? 'bg-green-600 text-white'
                : 'border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <input
          type="text"
          placeholder="Search by name, ID or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${inputClass} flex-1 min-w-48`}
        />
        <select value={clubFilter} onChange={(e) => setClubFilter(e.target.value)} className={inputClass}>
          <option value="">All Clubs</option>
          {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputClass}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Members List */}
      {filtered.length === 0 ? (
        <div className="text-gray-500 text-sm uppercase tracking-widest text-center py-10">
          No users found
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((member) => (
            <div
              key={member.id}
              className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-green-500/50 dark:hover:border-green-600/50 hover:bg-green-50 dark:hover:bg-green-900/10 transition-all px-6 py-4 flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-full overflow-hidden bg-green-900/50 border border-green-700/50 flex items-center justify-center text-white font-black text-sm shrink-0">
                {member.profile_photo ? (
                  <img src={member.profile_photo} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  member.full_name?.split(' ').map((n) => n[0]).join('').toUpperCase()
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-black text-gray-900 dark:text-white text-sm uppercase tracking-tight">
                    {member.full_name}
                  </span>
                  {roleBadge(member.role)}
                  <span className={`text-xs font-bold uppercase tracking-widest px-2 py-0.5 shrink-0 ${
                    member.status === 'active'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                  }`}>
                    {member.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                  <span className="text-green-600 dark:text-green-400 font-bold tracking-wider">
                    {member.membership_id}
                  </span>
                  <span className="hidden sm:block">{member.email}</span>
                  <span className="hidden md:block">{member.clubs?.name || 'No club'}</span>
                  <span className="hidden lg:block">{member.chapters?.name || 'No chapter'}</span>
                </div>
              </div>

              <div className="hidden lg:block text-xs text-gray-500 shrink-0">
                {new Date(member.joined_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </SuperAdminLayout>
  )
}